import { queryClient } from '@src/corelib/query';
import prefetchQuery from './prefetchQuery';
import { useQuery } from './useQuery';

const activityUrl = 'activity/';
const absenceParams = { acti_type: 'absence' };

import type { APIClient } from '@src/corelib/api';
import type { Absence, AbsenceDto } from '@src/types';
import useMutation from './useMutation';

const absenceUrl = `${activityUrl}absence/`;
const absenceTypeParams = { acty_type: 'absence' };
const typesUrl = `${activityUrl}types/`;

export function prefetchAbsences(client: APIClient) {
  prefetchQuery(activityUrl, client, { queryParams: absenceParams });
}

export function useAbsences() {
  const query = useQuery<AbsenceDto[], Absence[]>(activityUrl, {
    queryParams: absenceParams,
    select: (data: Array<AbsenceDto>) => data.map(absenceFromDto),
    initialData: [],
  });

  const { mutateAsync: bookAbsence } = useMutation<
    { absences: Absence[] },
    Error,
    Record<string, unknown>
  >({
    mutateFn: ({
      apiClient,
      ...rest
    }: { apiClient: APIClient } & Record<string, unknown>) => {
      return apiClient.request({
        url: absenceUrl,
        method: 'POST',
        payload: rest,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: ({ absences }: { absences: Absence[] }) => {
      queryClient.setQueryData(query.queryKey, (old: Absence[]) => [
        ...old,
        ...absences,
      ]);
    },
  });

  const { mutateAsync: deleteAbsences } = useMutation<
    { absences: Absence[] },
    Error,
    { ids: number[] }
  >({
    mutateFn: ({ apiClient, ids }: { apiClient: APIClient; ids: number[] }) => {
      return apiClient.request({
        url: absenceUrl,
        method: 'DELETE',
        payload: ids,
        bodyOnDelete: true,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: (
      { absences }: { absences: Absence[] },
      { ids }: { ids: number[] },
    ) => {
      queryClient.setQueryData(query.queryKey, (old: Absence[]) => {
        const updatesById = new Map(absences.map((a: Absence) => [a.id, a]));
        const result = [];
        for (const item of old) {
          if (ids.includes(item.id)) {
            continue;
          }
          const updated = updatesById.get(item.id);
          result.push(updated ?? item);
        }
        return result;
      });
    },
  });

  const { data: absences = [], ...restQuery } = query;
  return {
    absences,
    bookAbsence,
    deleteAbsences,
    ...restQuery,
  };
}

export function prefetchAbsenceTypes(client: APIClient) {
  prefetchQuery(typesUrl, client, { queryParams: absenceTypeParams });
}
export function useAbsenceTypes() {
  return useQuery(typesUrl, { queryParams: absenceTypeParams });
}

function absenceFromDto(absenceDto: AbsenceDto): Absence {
  return {
    id: absenceDto.id,
    userId: absenceDto.acti_user,
    typeId: absenceDto.acti_type,
    colour: absenceDto.acti_colour,
    date: new Date(absenceDto.acti_date),
    dateStr: absenceDto.acti_date,
    startTime: toAbsenceTime(absenceDto.acti_start_time),
    endTime: toAbsenceTime(absenceDto.acti_end_time),
    absenceTentative: absenceDto.acti_tentative,
    allDay: absenceDto.all_day,
    authorizedManager: absenceDto.acab_authorized_manager,
    authorizedHr: absenceDto.acab_authorized_hr,
    notes: absenceDto.acab_notes,
    export: absenceDto.acab_export,
    blockPosition: absenceDto.block_position,
    workdays: absenceDto.workday_count,
    showWorkdays: absenceDto.show_workday_count,
  };
}

function toAbsenceTime(time: string | null) {
  if (!time) return null;
  return time.slice(0, 5); // no seconds
}
